import React from 'react';
import {
    modals,
    useToast,
    ButtonGroup,
    Stack,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Box,
    Button,
    Text,
} from '@cad-arc';
import { FieldNameEnum, ReportingEventNumberResource } from '@mark43/cad-api';
import { createApiErrorHandler, useApiClient } from '@m43/core';
import { useEnabledFeatures } from './enabled-features/use-enabled-features';
import { useMutation } from '@tanstack/react-query';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { errorOccurredWhile } from '../helpers/error-occurred-while';

function formatPrimaryAgencyRensSummaryLine(
    numberOfRensForPrimaryAgency: number,
    renDisplayName: string
): string {
    if (numberOfRensForPrimaryAgency === 0) {
        return `No primary agency ${renDisplayName}s currently exist for this event.`;
    } else if (numberOfRensForPrimaryAgency === 1) {
        return `1 primary agency ${renDisplayName} currently exists for this event.`;
    } else {
        return `${numberOfRensForPrimaryAgency} primary agency ${renDisplayName}s currently exist for this event.`;
    }
}

function GenerateReportingEventNumberModal(
    props: Readonly<{
        onSave: () => Promise<unknown>;
        onClose: () => void;
        agencyEventNumber: string;
        numberOfRensForPrimaryAgency: number;
        isLoading?: boolean;
    }>
): React.ReactElement {
    const { onClose, agencyEventNumber, numberOfRensForPrimaryAgency, isLoading } = props;
    const { fieldDisplayNameByFieldName: fieldDisplayNames } = useBootstrapState();

    const renDisplayName =
        fieldDisplayNames[FieldNameEnum.AGENCY_EVENT_REPORTING_EVENT_NUMBER.name];

    const { multiRenGeneration } = useEnabledFeatures();

    return (
        <>
            <ModalHeader>Generate {renDisplayName}</ModalHeader>
            <ModalBody>
                <Box>
                    <Stack>
                        <Text>
                            Generate {renDisplayName} for Event {agencyEventNumber}?
                        </Text>
                        {multiRenGeneration ? (
                            <>
                                <Text>
                                    {formatPrimaryAgencyRensSummaryLine(
                                        numberOfRensForPrimaryAgency,
                                        renDisplayName
                                    )}
                                </Text>
                                <Text>This action cannot be reversed.</Text>
                            </>
                        ) : undefined}
                    </Stack>
                </Box>
            </ModalBody>
            <ModalFooter>
                <ButtonGroup>
                    <Button variant="ghost" onClick={onClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button
                        isLoading={isLoading}
                        variant="solid"
                        onClick={() => {
                            props.onSave().finally(() => {
                                onClose();
                            });
                        }}
                    >
                        Generate
                    </Button>
                </ButtonGroup>
            </ModalFooter>
        </>
    );
}

/**
 * Generate a ren for the primary agency of the supplied event
 *
 * This hook will also launch a confirmation modal when needed
 */
export function useGenerateReportingEventNumber() {
    const enabledFeatures = useEnabledFeatures();
    const apiClient = useApiClient();
    const toast = useToast();

    const { fieldDisplayNameByFieldName: fieldDisplayNames } = useBootstrapState();

    const renDisplayName =
        fieldDisplayNames[FieldNameEnum.AGENCY_EVENT_REPORTING_EVENT_NUMBER.name];

    const generateRen = useMutation((params: { agencyEventId: number }) => {
        return apiClient.request<ReportingEventNumberResource.GenerateReportingEventNumber>({
            method: 'POST',
            url: 'reporting_event_number/generate',
            data: { eventId: params.agencyEventId },
        });
    });

    const isLoading = [generateRen].some((mutation) => mutation.isLoading);

    return [
        ({
            agencyEventId,
            numberOfRensForPrimaryAgency,
            agencyEventNumber,
        }: {
            agencyEventNumber: string;
            agencyEventId: number;
            numberOfRensForPrimaryAgency: number;
        }) => {
            const onSave = () =>
                generateRen
                    .mutateAsync({
                        agencyEventId,
                    })
                    .then((resp) => {
                        const newReportingEventNumber =
                            resp.data.data?.reportingEventNumbers?.[0]?.reportingEventNumber;

                        toast({
                            status: 'default',
                            description: [
                                renDisplayName,
                                newReportingEventNumber,
                                `generated for ${agencyEventNumber}`,
                            ]
                                .filter(Boolean)
                                .join(' '),
                        });
                        return resp.data.data;
                    })
                    .catch(
                        createApiErrorHandler(() => {
                            toast({
                                status: 'error',
                                description: errorOccurredWhile(`generating ${renDisplayName}`),
                            });
                        })
                    );

            if (enabledFeatures.multiRenGeneration || enabledFeatures.promptBeforeGeneratingRen) {
                return modals.blank({
                    children: ({ onClose }) => {
                        return (
                            <GenerateReportingEventNumberModal
                                onClose={onClose}
                                agencyEventNumber={agencyEventNumber}
                                numberOfRensForPrimaryAgency={numberOfRensForPrimaryAgency}
                                onSave={onSave}
                                isLoading={isLoading}
                            />
                        );
                    },
                });
            } else {
                return onSave();
            }
        },
        { isLoading },
    ] as const;
}
